// CorpusHealthDashboard.js — v6.42.0 Corpus Depth Dashboard
// ==========================================================
// Super-admin only. Reflects /api/admin/corpus-health.
// Answers in one screen: what's in our corpus (grade × standard × form),
// what percentage has audio + printable PDFs, and how has it trended.
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => {
  const tok = localStorage.getItem("session_token");
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

// Severity color for the gap heatmap.
const gapColor = (count, threshold = 5) => {
  if (count === 0) return "#B91C1C";     // critical — deep red
  if (count < 3)   return "#EA580C";     // thin — orange
  if (count < threshold) return "#CA8A04"; // low — amber
  return "#16A34A";                        // adequate — green
};

// Standard short-names (WIDA ELD 1–5)
const STD_LABEL = {
  1: "S1 · Social & Instructional",
  2: "S2 · Language Arts",
  3: "S3 · Mathematics",
  4: "S4 · Science",
  5: "S5 · Social Studies",
};

// ─── mini SVG line chart (no external deps) ────────────────────────
function TrendLine({ series, height = 120, stroke = "#D97D54", label }) {
  if (!series || series.length < 2) {
    return (
      <div style={{ height, display: "flex", alignItems: "center",
        justifyContent: "center", color: "#94A3B8", fontSize: 12 }}>
        Take at least 2 snapshots to see a trend line.
      </div>
    );
  }
  const w = 320;
  const pad = 8;
  const values = series.map((s) => s.v);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const step = (w - pad * 2) / (series.length - 1);
  const scaleY = (v) =>
    height - pad - ((v - min) / (max - min || 1)) * (height - pad * 2);
  const pts = series.map((s, i) => `${pad + i * step},${scaleY(s.v)}`).join(" ");
  const areaPts = `${pad},${height - pad} ${pts} ${pad + (series.length - 1) * step},${height - pad}`;
  const last = values[values.length - 1];
  const first = values[0];
  const delta = last - first;
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${w} ${height}`}
         preserveAspectRatio="none" data-testid={`trend-${label}`}>
      <polygon points={areaPts} fill={stroke} opacity="0.10" />
      <polyline points={pts} fill="none" stroke={stroke}
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {series.map((s, i) => (
        <circle key={i} cx={pad + i * step} cy={scaleY(s.v)} r="2.5"
                fill={stroke} opacity={i === series.length - 1 ? 1 : 0.5} />
      ))}
      <text x={w - pad} y={14} textAnchor="end" fontSize="11"
            fill={delta >= 0 ? "#16A34A" : "#B91C1C"} fontWeight="600">
        {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(delta > 10 ? 0 : 2)}
      </text>
    </svg>
  );
}

// ─── KPI card ──────────────────────────────────────────────────────
function Kpi({ label, value, sub, tone = "slate", testid }) {
  const tones = {
    slate:  { bg: "#F8FAFC", fg: "#0F172A", border: "#E2E8F0" },
    green:  { bg: "#F0FDF4", fg: "#166534", border: "#BBF7D0" },
    orange: { bg: "#FFF7ED", fg: "#9A3412", border: "#FED7AA" },
    red:    { bg: "#FEF2F2", fg: "#7F1D1D", border: "#FECACA" },
  }[tone] || {};
  return (
    <div data-testid={testid} style={{
      background: tones.bg, color: tones.fg, border: `1px solid ${tones.border}`,
      borderRadius: 12, padding: "18px 20px", minWidth: 200,
    }}>
      <div style={{ fontSize: 11, letterSpacing: ".08em",
        textTransform: "uppercase", opacity: 0.7 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, marginTop: 6, lineHeight: 1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, marginTop: 8, opacity: 0.75 }}>{sub}</div>}
    </div>
  );
}

export default function CorpusHealthDashboard() {
  const [snap, setSnap] = useState(null);
  const [trend, setTrend] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [taking, setTaking] = useState(false);
  const [flash, setFlash] = useState(null);
  // v6.42.1 — Gap drill-down: clicking a heatmap cell or authoring-
  // priority row opens a slide-over showing every question in that
  // (grade × standard) bucket so content-ops can jump straight to the
  // next authoring target.
  const [drill, setDrill] = useState(null);          // { grade, standard }
  const [drillData, setDrillData] = useState(null);  // { count, questions }
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillErr, setDrillErr] = useState(null);

  const openDrill = (grade, standard) => {
    setDrill({ grade, standard });
    setDrillData(null);
    setDrillErr(null);
    setDrillLoading(true);
    axios.get(
      `${API}/api/admin/corpus-health/questions?grade=${encodeURIComponent(grade)}&standard=${standard}`,
      { headers: authHeader() },
    ).then((r) => setDrillData(r.data))
     .catch((e) => setDrillErr(e?.response?.data?.detail || "Failed to load questions."))
     .finally(() => setDrillLoading(false));
  };
  const closeDrill = () => { setDrill(null); setDrillData(null); setDrillErr(null); };

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/admin/corpus-health`, { headers: authHeader() }),
      axios.get(`${API}/api/admin/corpus-health/trend?days=${days}`, { headers: authHeader() }),
    ]).then(([a, b]) => { setSnap(a.data); setTrend(b.data); setErr(null); })
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load corpus health."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const takeSnapshot = async () => {
    setTaking(true);
    try {
      await axios.post(`${API}/api/admin/corpus-health/snapshot`, {},
        { headers: authHeader() });
      setFlash({ ok: true, msg: "Snapshot captured — trend line updated." });
      load();
    } catch (e) {
      setFlash({ ok: false, msg: e?.response?.data?.detail || "Snapshot failed." });
    } finally { setTaking(false); }
  };

  // Series for the two trend charts
  const audioSeries = useMemo(() =>
    (trend?.snapshots || []).map((s) => ({
      t: s.captured_at, v: s.coverage?.audio_pct ?? 0,
    })), [trend]);
  const booksSeries = useMemo(() =>
    (trend?.snapshots || []).map((s) => ({
      t: s.captured_at, v: s.coverage?.booklet_pct ?? 0,
    })), [trend]);
  const qsSeries = useMemo(() =>
    (trend?.snapshots || []).map((s) => ({
      t: s.captured_at, v: s.totals?.questions ?? 0,
    })), [trend]);

  const criticalGaps = (snap?.gaps || []).filter((g) => g.severity === "critical");
  const thinGaps = (snap?.gaps || []).filter((g) => g.severity === "thin");

  return (
    <div data-testid="corpus-health-page" style={{
      maxWidth: 1280, margin: "0 auto", padding: "32px 24px",
      fontFamily: "-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif",
      color: "#0F172A",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "flex-end", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Corpus Depth Dashboard</h1>
          <div style={{ color: "#64748B", fontSize: 13, marginTop: 4 }}>
            Questions by grade × standard × form · Audio & PDF coverage · Authoring gaps
            {snap?.captured_at && <> · Snapshot at {fmtDate(snap.captured_at)}</>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ fontSize: 12, color: "#64748B" }}>Trend window:</label>
          <select data-testid="corpus-trend-days" value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #CBD5E1",
              fontSize: 13, background: "#FFF" }}>
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>1 year</option>
          </select>
          <button data-testid="corpus-refresh-btn" onClick={load}
            style={{ padding: "9px 14px", border: "1px solid #CBD5E1",
              background: "#F8FAFC", borderRadius: 8, cursor: "pointer",
              fontSize: 13, color: "#334155" }}>
            Refresh
          </button>
          <button data-testid="corpus-snapshot-btn" onClick={takeSnapshot} disabled={taking}
            style={{ padding: "9px 16px", border: "none", background: "#1E293B",
              color: "#FFF", borderRadius: 8, cursor: taking ? "wait" : "pointer",
              fontSize: 13, fontWeight: 600, opacity: taking ? 0.6 : 1 }}>
            {taking ? "Capturing…" : "Take Snapshot"}
          </button>
        </div>
      </div>

      {flash && (
        <div data-testid="corpus-flash" style={{
          padding: "12px 16px", marginBottom: 16, borderRadius: 8,
          background: flash.ok ? "#F0FDF4" : "#FEF2F2",
          color: flash.ok ? "#166534" : "#7F1D1D", fontSize: 14,
        }}>{flash.msg}</div>
      )}

      {loading && (
        <div style={{ padding: 40, textAlign: "center", color: "#64748B" }}>
          Loading corpus depth…
        </div>
      )}
      {err && (
        <div data-testid="corpus-error" style={{
          padding: 16, background: "#FEF2F2", color: "#7F1D1D",
          borderRadius: 8, fontSize: 14,
        }}>{err}</div>
      )}

      {snap && !loading && (
        <>
          {/* KPI strip */}
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 28 }}>
            <Kpi testid="kpi-questions" label="Total questions"
                 value={snap.totals.questions.toLocaleString()}
                 sub={`across ${snap.grades.length} grade bands`} />
            <Kpi testid="kpi-audio" label="Listening audio coverage"
                 tone={snap.coverage.audio_pct >= 0.95 ? "green" :
                       snap.coverage.audio_pct >= 0.75 ? "orange" : "red"}
                 value={pct(snap.coverage.audio_pct)}
                 sub={`${snap.totals.audio_clips} clips · ${snap.totals.listening_qs} listening items`} />
            <Kpi testid="kpi-booklets" label="Booklet PDF coverage"
                 tone={snap.coverage.booklet_pct >= 0.95 ? "green" :
                       snap.coverage.booklet_pct >= 0.75 ? "orange" : "red"}
                 value={pct(snap.coverage.booklet_pct)}
                 sub={`${snap.totals.booklet_pdfs} baked / ${snap.totals.expected_booklets} expected`} />
            <Kpi testid="kpi-gaps" label="Authoring gaps"
                 tone={criticalGaps.length > 0 ? "red" :
                       thinGaps.length > 0 ? "orange" : "green"}
                 value={(snap.gaps || []).length}
                 sub={`${criticalGaps.length} critical · ${thinGaps.length} thin`} />
          </div>

          {/* Trend charts */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 16, marginBottom: 32 }}>
            <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: 18, background: "#FFF" }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                Question count · last {days}d
              </div>
              <TrendLine series={qsSeries} label="questions" stroke="#1E293B" />
              <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 6 }}>
                {trend?.count || 0} snapshot{(trend?.count || 0) === 1 ? "" : "s"} · latest {qsSeries.length ? qsSeries[qsSeries.length - 1].v.toLocaleString() : "—"}
              </div>
            </div>
            <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: 18, background: "#FFF" }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                Audio coverage · last {days}d
              </div>
              <TrendLine series={audioSeries} label="audio" stroke="#D97D54" />
              <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 6 }}>
                latest {audioSeries.length ? pct(audioSeries[audioSeries.length - 1].v) : "—"}
              </div>
            </div>
            <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: 18, background: "#FFF" }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                Booklet coverage · last {days}d
              </div>
              <TrendLine series={booksSeries} label="booklets" stroke="#0EA5E9" />
              <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 6 }}>
                latest {booksSeries.length ? pct(booksSeries[booksSeries.length - 1].v) : "—"}
              </div>
            </div>
          </div>

          {/* Grade × Standard depth heatmap */}
          <div style={{ border: "1px solid #E2E8F0", borderRadius: 12,
            background: "#FFF", overflow: "hidden", marginBottom: 24 }}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid #F1F5F9",
              fontWeight: 600, fontSize: 14 }}>
              Question depth · Grade × WIDA Standard
              <span style={{ fontSize: 11, color: "#94A3B8", marginLeft: 10, fontWeight: 400 }}>
                (cells with fewer than {snap.gap_threshold} items flagged as gaps)
              </span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table data-testid="corpus-heatmap" style={{ width: "100%",
                borderCollapse: "collapse", fontSize: 13 }}>
                <thead style={{ background: "#F8FAFC" }}>
                  <tr>
                    <th style={{ padding: "10px 14px", textAlign: "left",
                      fontWeight: 600, color: "#334155", borderBottom: "1px solid #E2E8F0" }}>
                      Grade
                    </th>
                    {snap.standards.map((s) => (
                      <th key={s} title={STD_LABEL[s]}
                        style={{ padding: "10px 14px", textAlign: "center",
                          fontWeight: 600, color: "#334155",
                          borderBottom: "1px solid #E2E8F0" }}>
                        S{s}
                      </th>
                    ))}
                    <th style={{ padding: "10px 14px", textAlign: "center",
                      fontWeight: 600, color: "#334155",
                      borderBottom: "1px solid #E2E8F0" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {snap.grades.map((g) => {
                    const row = snap.by_grade_std[g] || {};
                    const total = Object.values(row).reduce((a, b) => a + b, 0);
                    return (
                      <tr key={g}>
                        <td style={{ padding: "8px 14px", fontWeight: 600,
                          borderTop: "1px solid #F1F5F9" }}>{g}</td>
                        {snap.standards.map((s) => {
                          const n = row[String(s)] || 0;
                          const c = gapColor(n, snap.gap_threshold);
                          return (
                            <td key={s} data-testid={`cell-${g}-${s}`} style={{
                              padding: "6px 8px", textAlign: "center",
                              borderTop: "1px solid #F1F5F9",
                            }}>
                              <button
                                type="button"
                                onClick={() => openDrill(g, s)}
                                title={`Open ${n} question${n === 1 ? "" : "s"} for Grade ${g} · ${STD_LABEL[s]}`}
                                data-testid={`cell-drill-${g}-${s}`}
                                style={{
                                  display: "inline-block", minWidth: 44,
                                  padding: "4px 10px", borderRadius: 6,
                                  background: `${c}15`, color: c, fontWeight: 600,
                                  fontVariantNumeric: "tabular-nums",
                                  border: `1px solid ${c}30`, cursor: "pointer",
                                  transition: "transform .1s ease, box-shadow .1s ease",
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.transform = "translateY(-1px)";
                                  e.currentTarget.style.boxShadow = `0 4px 12px ${c}30`;
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.transform = "";
                                  e.currentTarget.style.boxShadow = "";
                                }}
                              >{n}</button>
                            </td>
                          );
                        })}
                        <td style={{ padding: "8px 14px", textAlign: "center",
                          fontWeight: 600, color: "#0F172A",
                          borderTop: "1px solid #F1F5F9",
                          fontVariantNumeric: "tabular-nums" }}>{total}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Grade × Form (A/B/C) */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
            gap: 16, marginBottom: 24 }}>
            <div style={{ border: "1px solid #E2E8F0", borderRadius: 12,
              background: "#FFF", overflow: "hidden" }}>
              <div style={{ padding: "14px 18px", borderBottom: "1px solid #F1F5F9",
                fontWeight: 600, fontSize: 14 }}>
                Form parity · Grade × Form A/B/C
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead style={{ background: "#F8FAFC" }}>
                    <tr>
                      <th style={{ padding: "10px 14px", textAlign: "left",
                        color: "#334155", fontWeight: 600,
                        borderBottom: "1px solid #E2E8F0" }}>Grade</th>
                      {snap.forms.map((f) => (
                        <th key={f} style={{ padding: "10px 14px", textAlign: "center",
                          color: "#334155", fontWeight: 600,
                          borderBottom: "1px solid #E2E8F0" }}>Form {f}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {snap.grades.map((g) => (
                      <tr key={g}>
                        <td style={{ padding: "8px 14px", fontWeight: 600,
                          borderTop: "1px solid #F1F5F9" }}>{g}</td>
                        {snap.forms.map((f) => {
                          const n = (snap.by_grade_form[g] || {})[f] || 0;
                          const c = gapColor(n, snap.gap_threshold);
                          return (
                            <td key={f} style={{ padding: "6px 8px",
                              textAlign: "center", borderTop: "1px solid #F1F5F9" }}>
                              <span style={{ display: "inline-block", minWidth: 40,
                                padding: "3px 10px", borderRadius: 6,
                                background: `${c}15`, color: c, fontWeight: 600,
                                fontVariantNumeric: "tabular-nums" }}>{n}</span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Gap alerts */}
            <div style={{ border: "1px solid #E2E8F0", borderRadius: 12,
              background: "#FFF", overflow: "hidden" }}>
              <div style={{ padding: "14px 18px", borderBottom: "1px solid #F1F5F9",
                fontWeight: 600, fontSize: 14 }}>
                Authoring priorities
                <span style={{ fontSize: 11, color: "#94A3B8", marginLeft: 10, fontWeight: 400 }}>
                  (sorted by severity)
                </span>
              </div>
              <div style={{ maxHeight: 320, overflowY: "auto" }}>
                {(snap.gaps || []).length === 0 && (
                  <div style={{ padding: 24, textAlign: "center", color: "#16A34A",
                    fontSize: 13 }}>
                    ✓ No gaps · every grade × standard cell has ≥ {snap.gap_threshold} items.
                  </div>
                )}
                {(snap.gaps || [])
                  .slice()
                  .sort((a, b) => a.count - b.count)
                  .map((g, i) => {
                    const c = gapColor(g.count, snap.gap_threshold);
                    return (
                      <button key={i}
                        type="button"
                        onClick={() => openDrill(g.grade, g.standard)}
                        data-testid={`gap-row-${g.grade}-${g.standard}`}
                        style={{ display: "flex", alignItems: "center",
                          width: "100%", textAlign: "left",
                          background: "transparent", border: "none",
                          padding: "10px 18px", borderTop: i === 0 ? 0 : "1px solid #F1F5F9",
                          fontSize: 13, cursor: "pointer",
                          transition: "background .1s ease" }}
                        onMouseOver={(e) => e.currentTarget.style.background = "#F8FAFC"}
                        onMouseOut={(e) => e.currentTarget.style.background = "transparent"}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%",
                          background: c, marginRight: 12 }} />
                        <div style={{ flex: 1 }}>
                          <span style={{ fontWeight: 600, color: "#0F172A" }}>Grade {g.grade}</span>
                          <span style={{ color: "#64748B" }}> · {STD_LABEL[g.standard]}</span>
                        </div>
                        <div style={{ color: c, fontWeight: 700,
                          fontVariantNumeric: "tabular-nums" }}>
                          {g.count} item{g.count === 1 ? "" : "s"}
                        </div>
                        <div style={{ marginLeft: 10, fontSize: 11, textTransform: "uppercase",
                          letterSpacing: ".06em", color: c, fontWeight: 600 }}>
                          {g.severity}
                        </div>
                      </button>
                    );
                })}
              </div>
            </div>
          </div>

          <div style={{ fontSize: 11, color: "#94A3B8", textAlign: "center",
            marginTop: 24 }}>
            Snapshots persist to <code>db.corpus_health_snapshots</code>.
            Cron writes a fresh row nightly at 06:15 UTC via{" "}
            <code>/api/cron/corpus-health-snapshot</code>.
          </div>
        </>
      )}

      {/* v6.42.1 — Gap Drill-Down slide-over */}
      {drill && (
        <>
          <div
            data-testid="drill-scrim"
            onClick={closeDrill}
            style={{
              position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.42)",
              zIndex: 40, animation: "fade-in .18s ease",
            }} />
          <aside
            data-testid="drill-drawer"
            style={{
              position: "fixed", top: 0, right: 0, bottom: 0,
              width: "min(720px, 96vw)", background: "#FFF", zIndex: 50,
              boxShadow: "-20px 0 60px rgba(15,23,42,.18)",
              display: "flex", flexDirection: "column",
              animation: "drill-slide .22s cubic-bezier(.2,.7,.2,1)",
            }}>
            <style>{`
              @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
              @keyframes drill-slide { from { transform: translateX(24px); opacity: 0; }
                                       to   { transform: translateX(0);    opacity: 1; } }
            `}</style>
            <header style={{ padding: "20px 24px", borderBottom: "1px solid #E2E8F0",
              display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: 11, letterSpacing: ".08em",
                  textTransform: "uppercase", color: "#64748B" }}>
                  Authoring queue
                </div>
                <h2 style={{ fontSize: 18, margin: "4px 0 0", color: "#0F172A" }}>
                  Grade {drill.grade} · {STD_LABEL[drill.standard]}
                </h2>
                {drillData && (
                  <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>
                    {drillData.count} item{drillData.count === 1 ? "" : "s"}
                    {drillData.count < (snap?.gap_threshold ?? 5) && (
                      <span style={{ color: "#B91C1C", marginLeft: 8 }}>
                        · below the {snap?.gap_threshold ?? 5}-item authoring floor
                      </span>
                    )}
                  </div>
                )}
              </div>
              <button data-testid="drill-close" onClick={closeDrill}
                style={{ background: "transparent", border: "none",
                  fontSize: 22, color: "#64748B", cursor: "pointer",
                  padding: 4, lineHeight: 1 }}>×</button>
            </header>

            <div style={{ flex: 1, overflowY: "auto", padding: "12px 24px 24px" }}>
              {drillLoading && (
                <div style={{ padding: 24, textAlign: "center", color: "#64748B", fontSize: 13 }}>
                  Loading questions…
                </div>
              )}
              {drillErr && (
                <div data-testid="drill-error" style={{ padding: 12,
                  background: "#FEF2F2", color: "#7F1D1D", borderRadius: 8, fontSize: 13 }}>
                  {drillErr}
                </div>
              )}
              {drillData && drillData.count === 0 && (
                <div style={{ padding: 40, textAlign: "center", color: "#0F172A", fontSize: 14 }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>⛔</div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Zero items in this cell.</div>
                  <div style={{ color: "#64748B", fontSize: 13 }}>
                    This is a <strong style={{ color: "#B91C1C" }}>critical gap</strong> —
                    the fastest path to close it is to seed 5 items through Content Studio.
                  </div>
                  <a href="/superadmin?tab=content-studio" target="_blank" rel="noreferrer"
                     data-testid="drill-open-studio"
                     style={{ display: "inline-block", marginTop: 16, padding: "10px 18px",
                       background: "#1E293B", color: "#FFF", borderRadius: 8,
                       fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
                    Open Content Studio →
                  </a>
                </div>
              )}
              {drillData && drillData.count > 0 && (
                <ul data-testid="drill-list" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {drillData.questions.map((q, idx) => {
                    const modColor = {
                      listening: "#0EA5E9", speaking: "#8B5CF6",
                      reading:   "#059669", writing:  "#D97D54",
                    }[q.modality] || "#64748B";
                    return (
                      <li key={q.question_id || idx}
                          data-testid={`drill-q-${idx}`}
                          style={{ border: "1px solid #E2E8F0", borderRadius: 10,
                            padding: "12px 14px", marginTop: idx === 0 ? 8 : 10,
                            background: "#FFF" }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center",
                          fontSize: 11, textTransform: "uppercase",
                          letterSpacing: ".06em", marginBottom: 6, flexWrap: "wrap" }}>
                          <span style={{ padding: "2px 8px", borderRadius: 999,
                            background: `${modColor}18`, color: modColor, fontWeight: 700 }}>
                            {q.modality || "—"}
                          </span>
                          <span style={{ color: "#64748B" }}>Form {q.form}</span>
                          {q.difficulty != null && (
                            <span style={{ color: "#64748B" }}>· L{q.difficulty}</span>
                          )}
                          {q.nyc_themed && (
                            <span style={{ padding: "2px 6px", borderRadius: 4,
                              background: "#FEF3C7", color: "#92400E", fontWeight: 700 }}>
                              NYC
                            </span>
                          )}
                          {q.authoring_method && (
                            <span style={{ marginLeft: "auto", color: "#94A3B8",
                              textTransform: "none", letterSpacing: 0 }}>
                              {q.authoring_method}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 13, color: "#0F172A", lineHeight: 1.45 }}>
                          {q.question_text || <em style={{ color: "#94A3B8" }}>no prompt</em>}
                        </div>
                        {q.stimulus_preview && (
                          <div style={{ fontSize: 12, color: "#64748B",
                            marginTop: 6, fontStyle: "italic" }}>
                            {q.stimulus_preview}
                          </div>
                        )}
                        {q.question_id && (
                          <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 8,
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                            id: {q.question_id}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <footer style={{ padding: "12px 24px", borderTop: "1px solid #E2E8F0",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              background: "#F8FAFC" }}>
              <div style={{ fontSize: 12, color: "#64748B" }}>
                Read-only view · edits happen in Content Studio.
              </div>
              <a href="/superadmin?tab=content-studio" target="_blank" rel="noreferrer"
                 data-testid="drill-open-studio-footer"
                 style={{ padding: "8px 14px", background: "#1E293B",
                   color: "#FFF", borderRadius: 8, fontSize: 13, fontWeight: 600,
                   textDecoration: "none" }}>
                Open Content Studio →
              </a>
            </footer>
          </aside>
        </>
      )}
    </div>
  );
}
